import { useRouter } from 'next/router'
import { useMemo } from 'react'

import { useCeritaPraktikTrackerParamsStore } from 'stores/cerita-praktik'
import { parseQuery } from 'utils/router'
import {
  TRACKER_QUERY_SOURCE_KEY,
  TRACKER_QUERY_SOURCE_VALUE_IDE_PRAKTIK,
} from '../constants'
import { useSatuEventTracker } from 'utils/satuEvent'

import type { SETrackEventCallbackArgs } from 'utils/satuEvent'
import type {
  ICeritaPraktikTrackerParams,
  TTrackerEventSource,
} from 'types/cerita-praktik'

export const useSETrackEvent = () => {
  const router = useRouter()
  const { SETrackEvent, SETransformAttrs } = useSatuEventTracker()
  const { trackerParams, setTrackerParams } =
    useCeritaPraktikTrackerParamsStore()

  const sourceParam: TTrackerEventSource = useMemo(() => {
    const source = parseQuery<string>(router.query?.[TRACKER_QUERY_SOURCE_KEY])

    return source === TRACKER_QUERY_SOURCE_VALUE_IDE_PRAKTIK
      ? 'IDE_PRAKTIK_PAGE'
      : 'EXTERNAL_LINK'
  }, [router.query])

  const SETrackOpenCeritaPraktikPage = ({
    category,
    contentId,
    revisionId,
  }: Pick<
    ICeritaPraktikTrackerParams,
    'category' | 'contentId' | 'revisionId'
  >) => {
    SETrackEvent({
      eventName: 'guru_ide_praktik_v1',
      sendUserData: true,
      eventParams: {
        activity_type: 'CERITA_PRAKTIK_PAGE',
        screen_name: 'CERITA_PRAKTIK_PAGE',
        event_trigger: sourceParam,
        attributes: SETransformAttrs({
          content_id: contentId,
          revision_id: revisionId,
          category,
        }),
      },
    })
  }

  const SETrackClickBack = (
    callback: (props: SETrackEventCallbackArgs) => void
  ) => {
    SETrackEvent({
      eventName: 'guru_ide_praktik_v1',
      sendUserData: true,
      eventParams: {
        activity_type: 'CLICK_BACK_BUTTON',
        screen_name: 'CERITA_PRAKTIK_PAGE',
        event_trigger: 'CERITA_PRAKTIK_PAGE',
        attributes: SETransformAttrs({
          category: trackerParams.category,
          content_id: trackerParams.contentId,
          creator_email: trackerParams.creatorEmails,
          event_source: sourceParam,
          num_cp_blocks: [trackerParams.numOfBlocks, 'int'],
          num_cp_images: [trackerParams.numOfImages, 'int'],
          num_cp_sections: [trackerParams.numOfSections, 'int'],
          num_cp_words: [trackerParams.numOfWords, 'int'],
          revision_id: trackerParams.revisionId,
        }),
      },
      callback,
      timeout: 500,
    })
  }

  const SETrackShare = ({ funnel }: { funnel: string }) => {
    SETrackEvent({
      eventName: 'guru_ide_praktik_v1',
      sendUserData: true,
      eventParams: {
        activity_type: 'SHARE_CERITA_PRAKTIK',
        event_trigger: 'CERITA_PRAKTIK_PAGE',
        screen_name: 'CERITA_PRAKTIK_PAGE',
        attributes: SETransformAttrs({
          category: trackerParams.category,
          content_id: trackerParams.contentId,
          creator_email: trackerParams.creatorEmails,
          event_source: sourceParam,
          num_cp_blocks: [trackerParams.numOfBlocks, 'int'],
          num_cp_images: [trackerParams.numOfImages, 'int'],
          num_cp_sections: [trackerParams.numOfSections, 'int'],
          num_cp_words: [trackerParams.numOfWords, 'int'],
          revision_id: trackerParams.revisionId,
          share_platform: funnel || '',
        }),
      },
    })
  }

  const SETrackRate = ({ rating }: { rating: 'USEFUL' | 'NOT_USEFUL' }) => {
    SETrackEvent({
      eventName: 'guru_ide_praktik_v1',
      sendUserData: true,
      eventParams: {
        activity_type: 'RATE_CERITA_PRAKTIK',
        event_trigger: 'CERITA_PRAKTIK_PAGE',
        screen_name: 'CERITA_PRAKTIK_PAGE',
        attributes: SETransformAttrs({
          category: trackerParams.category,
          content_id: trackerParams.contentId,
          creator_email: trackerParams.creatorEmails,
          event_source: sourceParam,
          feedback_rating: rating,
          num_cp_blocks: [trackerParams.numOfBlocks, 'int'],
          num_cp_images: [trackerParams.numOfImages, 'int'],
          num_cp_sections: [trackerParams.numOfSections, 'int'],
          num_cp_words: [trackerParams.numOfWords, 'int'],
          revision_id: trackerParams.revisionId,
        }),
      },
    })
  }

  const SETrackPassPixel = ({ blockOrder }: { blockOrder: number }) => {
    const pixelLocation: number = blockOrder / (trackerParams?.numOfBlocks ?? 0)

    SETrackEvent({
      eventName: 'guru_ide_praktik_v1',
      sendUserData: true,
      eventParams: {
        activity_type: 'PIXEL_CERITA_PRAKTIK',
        event_trigger: 'CERITA_PRAKTIK_PAGE',
        screen_name: 'CERITA_PRAKTIK_PAGE',
        attributes: SETransformAttrs({
          category: trackerParams.category,
          content_id: trackerParams.contentId,
          creator_email: trackerParams.creatorEmails,
          event_source: sourceParam,
          num_cp_blocks: [trackerParams.numOfBlocks, 'int'],
          num_cp_images: [trackerParams.numOfImages, 'int'],
          num_cp_sections: [trackerParams.numOfSections, 'int'],
          num_cp_words: [trackerParams.numOfWords, 'int'],
          pixel_location: [pixelLocation, 'float'],
          revision_id: trackerParams.revisionId,
        }),
      },
    })
  }

  return {
    SETrackClickBack,
    SETrackOpenCeritaPraktikPage,
    setTrackerParams,
    SETrackPassPixel,
    SETrackRate,
    SETrackShare,
  }
}
